import React from "react"
import {round} from "lodash"
import {Row, Col} from "antd"
import {Auth} from "types/auth"
import {getStudentAcademicScoreValues, toDateOnly} from "helpers"
import styles from "./StudentProgress.module.css"
import {BaseLoading} from "components"
import {useTranslation} from "react-i18next"
import moment from "moment"
import {useModel, useStudentFieldLabels} from "hooks"

type Props = {
    studentStats: Auth.StudentStatistics
    isRightPanel?: boolean
}

export function StudentScore(props: Props) {
    const {studentStats, isRightPanel = false} = props
    const {studentLabelInfo, labelKey, isLoading} = useStudentFieldLabels()
    const academicScoreValues = getStudentAcademicScoreValues(studentStats)
    const {t} = useTranslation(["common"])
    const model = useModel()
    const userDateFormat = model.getUserDateFormat()

    if (isLoading || !studentLabelInfo) {
        return (
            <div className={styles.loadingWrapper}>
                <BaseLoading isShow />
            </div>
        )
    }

    const {
        gpa,
        cgpa,
        attendancePercentage,
        maxTimeframe,
        missedAttendanceHours,
        missedAttendanceDays,
        makeUpHours,
        hoursToMakeup
    } = academicScoreValues

    return (
        <Row gutter={[12, 8]}>
            <Col span={isRightPanel ? 24 : 12}>
                <div className={styles.scoreInfoWrap}>
                    {studentLabelInfo["academics.gpa"]?.isActive && (
                        <div className={styles.scoreInfoItem}>
                            <span className={styles.scoreTitle}>{studentLabelInfo["academics.gpa"][labelKey]}</span>
                            <span className={styles.score}>{gpa}</span>
                        </div>
                    )}
                    {studentLabelInfo["academics.cgpa"]?.isActive && (
                        <div className={styles.scoreInfoItem}>
                            <span className={styles.scoreTitle}>{studentLabelInfo["academics.cgpa"][labelKey]}</span>
                            <span className={styles.score}>{cgpa}</span>
                        </div>
                    )}
                </div>
            </Col>
            {studentLabelInfo["academics.attendance_percentage"]?.isActive && (
                <Col span={isRightPanel ? 24 : 12}>
                    <div className={styles.scoreInfo}>
                        <span className={styles.scoreTitle}>
                            {studentLabelInfo["academics.attendance_percentage"][labelKey]}
                        </span>
                        <span className={styles.score}>{attendancePercentage}</span>
                    </div>
                </Col>
            )}
            {studentLabelInfo["academics.max_timeframe_percentage"]?.isActive && (
                <Col span={isRightPanel ? 24 : 12}>
                    <div className={styles.scoreInfo}>
                        <span className={styles.scoreTitle}>
                            {studentLabelInfo["academics.max_timeframe_percentage"][labelKey]}
                        </span>
                        <span className={styles.score}>{maxTimeframe}</span>
                    </div>
                </Col>
            )}
            {studentLabelInfo["academics.missed_attendance_hours"]?.isActive && (
                <Col span={isRightPanel ? 24 : 12}>
                    <div className={styles.scoreInfo}>
                        <span className={styles.scoreTitle}>
                            {studentLabelInfo["academics.missed_attendance_hours"][labelKey]}
                        </span>
                        <span className={styles.score}>{missedAttendanceHours}</span>
                    </div>
                </Col>
            )}
            {studentLabelInfo["academics.missed_attendance_days"]?.isActive && (
                <Col span={isRightPanel ? 24 : 12}>
                    <div className={styles.scoreInfo}>
                        <span className={styles.scoreTitle}>
                            {studentLabelInfo["academics.missed_attendance_days"][labelKey]}
                        </span>
                        <span className={styles.score}>{missedAttendanceDays}</span>
                    </div>
                </Col>
            )}
            {studentLabelInfo["academics.make_up_attendance_hours"]?.isActive && (
                <Col span={isRightPanel ? 24 : 12}>
                    <div className={styles.scoreInfo}>
                        <span className={styles.scoreTitle}>
                            {studentLabelInfo["academics.make_up_attendance_hours"][labelKey]}
                        </span>
                        <span className={styles.score}>{makeUpHours}</span>
                    </div>
                </Col>
            )}
            {studentLabelInfo["academics.hours_to_make_up"]?.isActive && (
                <Col span={isRightPanel ? 24 : 12}>
                    <div className={styles.scoreInfo}>
                        <span className={styles.scoreTitle}>
                            {studentLabelInfo["academics.hours_to_make_up"][labelKey]}
                        </span>
                        <span className={styles.score}>{hoursToMakeup}</span>
                    </div>
                </Col>
            )}
            <Col span={isRightPanel ? 24 : 12}>
                <div className={styles.scoreInfo}>
                    <span className={styles.scoreTitle}>Track</span>
                    <span className={styles.score}>{studentStats.academicTrack ?? ""}</span>
                </div>
            </Col>
            {studentLabelInfo["academics.phase"]?.isActive && (
                <Col span={isRightPanel ? 24 : 12}>
                    <div className={styles.scoreInfo}>
                        <span className={styles.scoreTitle}>{studentLabelInfo["academics.phase"][labelKey]}</span>
                        <span className={styles.score}>
                            {studentStats.phases?.map((item) => item.name)?.join(", ") ?? ""}
                        </span>
                    </div>
                </Col>
            )}
            {studentLabelInfo["academics.shift"]?.isActive && (
                <Col span={isRightPanel ? 24 : 12}>
                    <div className={styles.scoreInfo}>
                        <span className={styles.scoreTitle}>{studentLabelInfo["academics.shift"][labelKey]}</span>
                        <span className={styles.score}>
                            {studentStats.shifts?.map((item) => item.name)?.join(", ") ?? ""}
                        </span>
                    </div>
                </Col>
            )}
            {studentLabelInfo["academics.sap_status"]?.isActive && (
                <>
                    <Col span={isRightPanel ? 24 : 12}>
                        <div className={styles.scoreInfo}>
                            <span className={styles.scoreTitle}>
                                {studentLabelInfo["academics.sap_status"][labelKey]}
                            </span>
                            <span className={styles.score}>
                                {studentStats?.sapStatus ? t(`common:sapStatus.${studentStats.sapStatus}`) : ""}
                            </span>
                        </div>
                    </Col>
                    <Col span={isRightPanel ? 24 : 12}>
                        <div className={styles.scoreInfo}>
                            <span className={styles.scoreTitle}>SAP Date</span>
                            <span className={styles.score}>
                                {studentStats?.sapDate
                                    ? moment(toDateOnly(studentStats.sapDate)).format(userDateFormat)
                                    : ""}
                            </span>
                        </div>
                    </Col>
                </>
            )}
            {studentLabelInfo["academics.fa_sap_status"]?.isActive && (
                <>
                    <Col span={isRightPanel ? 24 : 12}>
                        <div className={styles.scoreInfo}>
                            <span className={styles.scoreTitle}>
                                {studentLabelInfo["academics.fa_sap_status"][labelKey]}
                            </span>
                            <span className={styles.score}>
                                {studentStats?.faSapStatus ? t(`common:sapStatus.${studentStats.faSapStatus}`) : ""}
                            </span>
                        </div>
                    </Col>
                    <Col span={isRightPanel ? 24 : 12}>
                        <div className={styles.scoreInfo}>
                            <span className={styles.scoreTitle}>FA SAP Date</span>
                            <span className={styles.score}>
                                {studentStats?.faSapDate
                                    ? moment(toDateOnly(studentStats.faSapDate)).format(userDateFormat)
                                    : ""}
                            </span>
                        </div>
                    </Col>
                </>
            )}
            {studentLabelInfo["academics.sap_percentage"]?.isActive && (
                <Col span={isRightPanel ? 24 : 12}>
                    <div className={styles.scoreInfo}>
                        <span className={styles.scoreTitle}>
                            {studentLabelInfo["academics.sap_percentage"][labelKey]}
                        </span>
                        <span className={styles.score}>{round((studentStats?.sapPercentage ?? 0) * 100, 1)}%</span>
                    </div>
                </Col>
            )}
        </Row>
    )
}
